import type { ReactNode } from "react";
import { useCallback, useEffect } from "react";
import FullPageLoading from "../../components/shared/loading/FullPageLoading";
import { Variables } from "../configs/appConfigs";
import LocalStorageService from "../helpers/localStorageService";
import { parseJWTToken, setTokensInStorage } from "../helpers/tokenService";
import { GET_DEVICE_ID, GET_USER_SUBSCRIPTION_DETAIL, LOGOUT } from "../graphql/authGraphQL";
import { useAuth, useAuthMutate } from "./authHooks";
import type { ParsedAccessToken } from "./authInterfaces";
import { setUserInfo, setUserLoading } from "./userDataActions";
import useQueryFetch from "../api/apiHooks/useQueryFetch";
import { getProductIdHeader, getAtkHeader, getRtkHeader } from "../api/fetch/getHeaders";
import handleRefreshToken from "../api/fetch/handleRefreshToken";
import { useToast } from "../toast/context/toastContextHooks";
import { ToastActionType } from "../toast/context/toast.interface";

interface IProps {
  children: ReactNode;
}

const AuthWrapper = (props: IProps) => {
  const dispatch = useAuthMutate();
  const accessToken = LocalStorageService.get(Variables.accessToken);
  const refreshToken = LocalStorageService.get(Variables.refreshToken);
  const auth = useAuth();
  const toast = useToast();
  const [getDeviceId] = useQueryFetch<{
    data: { getDid: { data: { did: string } } };
  }>(process.env.TV_AUTH_GRAPHQL_ENDPOINT, GET_DEVICE_ID, getProductIdHeader);
  const [getSubscriptionDetails] = useQueryFetch(
    process.env.TV_ACCOUNT_URL,
    GET_USER_SUBSCRIPTION_DETAIL,
    getAtkHeader,
    true,
  );

  const [mutateLogout] = useQueryFetch(process.env.TV_AUTH_GRAPHQL_ENDPOINT, LOGOUT, getRtkHeader);

  const setUserContext = useCallback(
    (accessToken: string, remainingDays?: number) => {
      const userData = parseJWTToken(accessToken) as ParsedAccessToken;
      dispatch(
        setUserInfo({
          sub: userData.sub,
          ap: userData.ap,
          m: userData.m,
          e: userData.e,
          rs: userData.rs,
          asp: userData.asp,
          ao: userData.ao,
          op: userData.op,
          asrd: remainingDays,
        }),
      );
    },
    [dispatch],
  );

  const getDid = async () => {
    const res = await getDeviceId();
    LocalStorageService.set(Variables.did, res.data.getDid.data.did);
  };

  const getAuthenticationPrerequisites = useCallback(async () => {
    const deviceId = localStorage.getItem(Variables.did);
    if (!deviceId) {
      try {
        await getDid();
      } catch (err: any) {
        toast({
          message: err ? JSON.stringify(err) : "خطایی رخ داده",
          options: {
            duration: 100000,
            type: ToastActionType.ERROR,
          },
        });
      }
    }
    if (accessToken) {
      try {
        const res = await getSubscriptionDetails();
        const subscriptionDetails = res.data.getUserSubscriptionDetails.data;
        const userData = parseJWTToken(accessToken) as ParsedAccessToken;

        if (subscriptionDetails.remainingDays > 0 && !userData.asp?.id) {
          const tokens: any = await handleRefreshToken();
          if (tokens?.data?.refreshToken?.data) {
            setTokensInStorage(tokens.data.refreshToken.data);
            setUserContext(tokens.data.refreshToken.data.atk, subscriptionDetails.remainingDays);
          }
        } else {
          setUserContext(accessToken, subscriptionDetails.remainingDays);
        }
        dispatch(setUserLoading(false));
      } catch (error) {
        LocalStorageService.delete(Variables.accessToken);
        LocalStorageService.delete(Variables.refreshToken);
        dispatch(setUserLoading(false));
      }
    } else {
      if (!refreshToken) {
        dispatch(setUserLoading(false));
      } else {
        try {
          const tokens: any = await handleRefreshToken();
          if (tokens?.data?.refreshToken?.data) {
            setTokensInStorage(tokens.data.refreshToken.data);
            const res = await getSubscriptionDetails();
            const subscriptionDetails = res.data.getUserSubscriptionDetails.data;
            setUserContext(tokens.data.refreshToken.data.atk, subscriptionDetails.remainingDays);
          }
          dispatch(setUserLoading(false));
        } catch (error) {
          try {
            await mutateLogout();
          } catch (err: any) {
            console.log("Error logging out user. Reason: " + err.message);
          } finally {
            LocalStorageService.delete(Variables.accessToken);
            LocalStorageService.delete(Variables.refreshToken);
            dispatch(setUserLoading(false));
          }
        }
      }
    }
  }, []);

  useEffect(() => {
    getAuthenticationPrerequisites();
  }, []);

  return auth.loading ? <FullPageLoading loadingText="در حال ورود" /> : props.children;
};

export default AuthWrapper;
